import {
  ApiOutlined,
  BankOutlined,
  BookOutlined,
  CalendarOutlined,
  FileOutlined,
  HeartOutlined,
  HomeOutlined,
  SafetyOutlined,
  SettingOutlined,
  ToolOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import React from 'react';

import AscensoresPage from 'pages/admin/IDClientePage/AscensoresPage';
import Carpeta0 from 'pages/admin/IDClientePage/Carpeta0';
import CarpetaDigital from 'pages/admin/IDClientePage/CarpetaDigital';
import ClienteActividadesPageWrapper from 'pages/admin/IDClientePage/ClienteActividadesPageWrapper';
import ClienteInicioPage from 'pages/admin/IDClientePage/ClienteInicioPage';
import ClienteMantencionesPage from 'pages/admin/IDClientePage/ClienteMantencionesPage';
import EdificiosPage from 'pages/admin/IDClientePage/EdificiosPage';
import EmergenciasPage from 'pages/admin/IDClientePage/EmergenciasPage';
import HallazgosPage from 'pages/admin/IDClientePage/HallazgosPage';
import ODTPage from 'pages/admin/IDClientePage/ODTPage';
import SaludPage from 'pages/admin/IDClientePage/SaludPage';
import ClienteDashboard from 'pages/administrador/components/ClienteDashboard';

// Diccionario de iconos disponibles. La DB guarda el nombre (string);
// este mapa lo resuelve a un elemento React. Cualquier icono nuevo que
// se quiera usar como tab tiene que sumarse acá.
const ICON_MAP = {
  HomeOutlined: <HomeOutlined />,
  HeartOutlined: <HeartOutlined />,
  WarningOutlined: <WarningOutlined />,
  ToolOutlined: <ToolOutlined />,
  SettingOutlined: <SettingOutlined />,
  SafetyOutlined: <SafetyOutlined />,
  FileOutlined: <FileOutlined />,
  CalendarOutlined: <CalendarOutlined />,
  BookOutlined: <BookOutlined />,
  UserOutlined: <UserOutlined />,
  BankOutlined: <BankOutlined />,
  ApiOutlined: <ApiOutlined />,
};

export const resolveIcon = (iconName) => {
  if (!iconName) return null;
  return ICON_MAP[iconName] ?? null;
};

export const AVAILABLE_ICONS = Object.keys(ICON_MAP);

// Registry key → componente por userType. Si un key tiene `null` para un
// userType, no se renderiza aunque esté en la DB — permite cascada sin
// forzar a ejec a ver tabs que no tienen sentido para su rol.
export const CLIENT_TABS_REGISTRY = {
  general: {
    ejecutivo: ClienteInicioPage,
    administrador: null,
    defaultLabel: 'General',
    defaultIcon: 'HomeOutlined',
  },
  dashboard: {
    ejecutivo: null,
    administrador: ClienteDashboard,
    defaultLabel: 'Dashboard',
    defaultIcon: 'HomeOutlined',
  },
  informacion: {
    ejecutivo: null,
    administrador: ClienteInicioPage,
    defaultLabel: 'Información',
    defaultIcon: 'UserOutlined',
  },
  salud: {
    ejecutivo: SaludPage,
    supervisor: SaludPage,
    administrador: null,
    defaultLabel: 'Salud',
    defaultIcon: 'HeartOutlined',
  },
  hallazgos: {
    ejecutivo: HallazgosPage,
    supervisor: HallazgosPage,
    administrador: null,
    defaultLabel: 'Hallazgos',
    defaultIcon: 'WarningOutlined',
  },
  edificios: {
    ejecutivo: EdificiosPage,
    supervisor: EdificiosPage,
    administrador: EdificiosPage,
    defaultLabel: 'Edificios',
    defaultIcon: 'BankOutlined',
  },
  ascensores: {
    ejecutivo: AscensoresPage,
    supervisor: AscensoresPage,
    administrador: AscensoresPage,
    defaultLabel: 'Equipos',
    defaultIcon: 'ApiOutlined',
  },
  mantenciones: {
    ejecutivo: ClienteMantencionesPage,
    supervisor: ClienteMantencionesPage,
    administrador: ClienteMantencionesPage,
    defaultLabel: 'Mantenciones',
    defaultIcon: 'SettingOutlined',
  },
  emergencias: {
    ejecutivo: EmergenciasPage,
    supervisor: EmergenciasPage,
    administrador: EmergenciasPage,
    defaultLabel: 'Emergencias',
    defaultIcon: 'SafetyOutlined',
  },
  odt: {
    ejecutivo: ODTPage,
    administrador: ODTPage,
    defaultLabel: 'ODT',
    defaultIcon: 'FileOutlined',
  },
  actividades: {
    ejecutivo: ClienteActividadesPageWrapper,
    supervisor: ClienteActividadesPageWrapper,
    administrador: null,
    defaultLabel: 'Plan de Mantenimiento',
    defaultIcon: 'CalendarOutlined',
  },
  'carpeta-digital': {
    ejecutivo: CarpetaDigital,
    supervisor: CarpetaDigital,
    administrador: null,
    defaultLabel: 'Carpeta Digital',
    defaultIcon: 'BookOutlined',
  },
  'carpeta-0': {
    ejecutivo: Carpeta0,
    supervisor: Carpeta0,
    administrador: Carpeta0,
    defaultLabel: 'Carpeta Cero',
    defaultIcon: 'BookOutlined',
  },
};

export const getTabComponent = (key, userType) => CLIENT_TABS_REGISTRY[key]?.[userType] ?? null;

// Tabs de la ficha de cliente que son features del plan Pro: sus endpoints están
// gateados por requirePlan('pro') en el backend (hallazgos → /actividades-hallazgo,
// odt → /order). En plan lite:
//   - Para `administrador`: se OCULTAN (portal solo-consulta; el upsell no aplica).
//   - Para `ejecutivo`: se muestran con `locked: true` → ProLockOverlay (es quien
//     puede contratar el plan).
// (El tab `salud` consume /kpi/cliente/:id/salud, que NO está gateado, así que
// queda disponible en lite para ambos roles.)
export const PRO_TAB_KEYS = ['hallazgos', 'odt'];

export const isProTab = (key) => PRO_TAB_KEYS.includes(key);

export const getTabDefaults = (key) => {
  const entry = CLIENT_TABS_REGISTRY[key];
  if (!entry) return null;
  return { label: entry.defaultLabel, icon: entry.defaultIcon };
};

export const listRegistryKeys = () => Object.keys(CLIENT_TABS_REGISTRY);
